import { StyleSheet } from 'aphrodite';
import { SPACE, THEME } from '../../../components-v2/Theme/theme';
import { charmGray, grayIconFill, mention, titles } from '../../../styles/colors';
import { shadowLg } from '../../../styles/shadows';
import { CssSelectors } from '../../../styles/styles';

// Keep the textarea and highlight layer text metrics identical.
const COMPOSER_TEXT_BORDER_WIDTH = 1;
const COMPOSER_TEXT_FONT_SIZE = THEME.typography.fontSizes.sm;
const COMPOSER_TEXT_PADDING_TOP = 10;
const COMPOSER_TEXT_PADDING_X = 14;
const COMPOSER_TEXT_PADDING_BOTTOM = 14;

// Matches the textarea's global input style.
const COMPOSER_TEXT_FONT_FAMILY = '"Sora", sans-serif';

const COMPOSER_TRANSITION = '140ms cubic-bezier(.4, 0, .2, 1)';

export const styleSheet = StyleSheet.create({
	composerMeters: {
		alignItems: 'center',
		color: THEME.colors.text.light,
		display: 'flex',
		flexShrink: 0,
		fontSize: THEME.typography.fontSizes.xs,
		gap: SPACE[8],
		justifyContent: 'flex-end',
		marginLeft: 'auto',
		...CssSelectors.allDescendants({ display: 'flex' }, 'button'),
	},
	// The body, the media and the limits are one version's, so one surface holds all three and the
	// tabs above pick which version that is.
	versionPanel: {
		display: 'flex',
		flexDirection: 'column',
		gap: SPACE[32],
		paddingTop: SPACE[16],
	},
	versionPanelSplit: {
		background: THEME.colors.background.light,
		border: `1px solid ${THEME.colors.border.main}`,
		// The bar above already draws this edge; a second one would double the rule.
		borderTop: 'none',
		borderRadius: `0 0 ${THEME.common.borderRadius}px ${THEME.common.borderRadius}px`,
		padding: SPACE[24],
	},
	container: {
		color: grayIconFill,
		fontSize: 14,
		marginBottom: 72,
	},
	header: {
		color: titles,
		fontSize: 18,
	},
	headerContainer: {
		display: 'flex',
		justifyContent: 'space-between',
		marginBottom: 20,
	},
	labelEmoji: {
		alignItems: 'center',
		display: 'flex',
	},
	composerLabelRow: {
		alignItems: 'center',
		color: charmGray,
		display: 'flex',
		fontSize: THEME.typography.fontSizes.sm,
		gap: SPACE[16],
		justifyContent: 'space-between',
		marginBottom: SPACE[8],
		minHeight: 32,
	},
	composerField: {
		background: THEME.colors.background.light,
		border: `1px solid ${THEME.colors.border.main}`,
		borderRadius: THEME.common.borderRadius,
		display: 'flex',
		flexDirection: 'column',
		overflow: 'hidden',
		transition: `border-color ${COMPOSER_TRANSITION}`,
		':focus-within': {
			borderColor: THEME.colors.primary.main,
		},
	},
	composerFieldOverLimit: {
		borderColor: THEME.colors.danger.main,
		':focus-within': {
			borderColor: THEME.colors.danger.main,
		},
	},
	characterCount: {
		cursor: 'help',
		fontVariantNumeric: 'tabular-nums',
	},
	characterCountOverLimit: {
		color: THEME.colors.danger.main,
		fontWeight: THEME.typography.fontWeights.bold,
	},
	mediaContainer: {
		color: charmGray,
		fontSize: 14,
	},
	mentionAnchor: {
		height: 1,
		pointerEvents: 'none',
		position: 'absolute',
		width: 1,
		zIndex: 2,
	},
	mentionDropdown: {
		...THEME.common.scrollBar,
		background: THEME.colors.background.light,
		border: THEME.common.border,
		borderRadius: THEME.common.borderRadius,
		boxShadow: shadowLg,
		boxSizing: 'border-box',
		fontFamily: THEME.typography.fontFamilies.lexend,
		maxHeight: 280,
		minWidth: 320,
		overflowY: 'auto',
		padding: THEME.spacing.xs,
	},
	mentionEmptyState: {
		color: THEME.colors.text.light,
		fontFamily: THEME.typography.fontFamilies.lexend,
		fontSize: THEME.typography.fontSizes.sm,
		padding: `${THEME.spacing.sm}px ${THEME.spacing.md}px`,
	},
	// Box shadow adds fill without changing text layout.
	mentionHighlight: {
		background: mention,
		borderRadius: 4,
		boxShadow: `0 0 0 2px ${mention}`,
	},
	captionCount: {
		color: THEME.colors.text.light,
		cursor: 'help',
		fontSize: THEME.typography.fontSizes.xs,
		fontVariantNumeric: 'tabular-nums',
	},
	captionCountOverLimit: {
		color: THEME.colors.warning.main,
		fontWeight: THEME.typography.fontWeights.bold,
	},
	mentionHighlightLayer: {
		backgroundColor: 'transparent',
		borderColor: 'transparent',
		borderRadius: 6,
		borderStyle: 'solid',
		borderWidth: COMPOSER_TEXT_BORDER_WIDTH,
		bottom: 0,
		boxSizing: 'border-box',
		color: 'transparent',
		fontFamily: COMPOSER_TEXT_FONT_FAMILY,
		fontSize: COMPOSER_TEXT_FONT_SIZE,
		fontStyle: 'normal',
		fontWeight: 'normal',
		left: 0,
		letterSpacing: 'normal',
		lineHeight: 'normal',
		overflow: 'hidden',
		paddingBottom: COMPOSER_TEXT_PADDING_BOTTOM,
		paddingLeft: COMPOSER_TEXT_PADDING_X,
		paddingRight: COMPOSER_TEXT_PADDING_X,
		paddingTop: COMPOSER_TEXT_PADDING_TOP,
		pointerEvents: 'none',
		position: 'absolute',
		right: 0,
		textAlign: 'left',
		textIndent: 0,
		top: 0,
		whiteSpace: 'pre-wrap',
		wordSpacing: 'normal',
		wordWrap: 'break-word',
	},
	mentionHighlightLayerDisabled: {
		opacity: 0.5,
	},
	mentionBubble: {
		background: THEME.colors.background.light,
		border: THEME.common.border,
		borderRadius: THEME.common.borderRadius,
		boxShadow: shadowLg,
		boxSizing: 'border-box',
		display: 'flex',
		flexDirection: 'column',
		fontFamily: THEME.typography.fontFamilies.lexend,
		gap: SPACE[4],
		maxWidth: 320,
		padding: `${THEME.spacing.sm}px ${THEME.spacing.md}px`,
		position: 'absolute',
		zIndex: 3,
	},
	mentionDisclaimer: {
		color: THEME.colors.text.light,
		fontFamily: THEME.typography.fontFamilies.lexend,
		fontSize: THEME.typography.fontSizes.xs,
		lineHeight: 1.4,
		paddingTop: THEME.spacing.sm,
	},
	mentionBubbleLink: {
		color: THEME.colors.primary.main,
		fontSize: THEME.typography.fontSizes.xs,
		textDecoration: 'underline',
		width: 'fit-content',
	},
	mentionResultItem: {
		alignItems: 'flex-start',
		background: 'transparent',
		border: 'none',
		color: THEME.colors.text.main,
		cursor: 'pointer',
		display: 'flex',
		flex: '1 1 auto',
		flexDirection: 'column',
		gap: SPACE[4],
		minWidth: 0,
		padding: `${THEME.spacing.sm}px ${THEME.spacing.md}px`,
		textAlign: 'left',
	},
	mentionResultLink: {
		color: THEME.colors.primary.main,
		flex: '0 0 auto',
		fontSize: THEME.typography.fontSizes.xs,
		padding: `0 ${THEME.spacing.md}px`,
		textDecoration: 'underline',
	},
	mentionResultMeta: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.xs,
	},
	mentionResultName: {
		color: THEME.colors.text.main,
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.bold,
	},
	mentionResultNameRow: {
		alignItems: 'center',
		display: 'flex',
		gap: THEME.spacing.sm,
		width: '100%',
	},
	mentionResultRow: {
		alignItems: 'center',
		borderRadius: THEME.common.borderRadius - 2,
		display: 'flex',
		justifyContent: 'space-between',
		transition: 'background-color 0.1s ease-in-out',
		':hover': {
			background: THEME.colors.hover.light,
		},
	},
	mentionResultRowHighlighted: {
		background: THEME.colors.hover.light,
	},
	mentionSectionTitle: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.xxs,
		letterSpacing: 0.4,
		padding: `${THEME.spacing.sm}px ${THEME.spacing.md}px ${THEME.spacing.xs}px`,
		textTransform: 'uppercase',
	},
	modal: {
		...CssSelectors.allDescendants(
			{
				padding: 0,
			},
			'.modal-overlay-content'
		),
	},
	textAreaContainer: {
		background: 'transparent',
		// The highlight layer mirrors this box, so the border stays and only gives up its color.
		borderColor: 'transparent',
		borderRadius: 0,
		fieldSizing: 'content',
		fontFamily: COMPOSER_TEXT_FONT_FAMILY,
		fontSize: COMPOSER_TEXT_FONT_SIZE,
		// A resting height with room to draft in. `fieldSizing: content` grows the box past this, so
		// it only sets where an empty or short post starts.
		minHeight: 240,
		paddingBottom: COMPOSER_TEXT_PADDING_BOTTOM,
		paddingLeft: COMPOSER_TEXT_PADDING_X,
		paddingRight: COMPOSER_TEXT_PADDING_X,
		paddingTop: COMPOSER_TEXT_PADDING_TOP,
	},
	textAreaOverHighlights: {
		background: 'transparent',
		position: 'relative',
		zIndex: 1,
	},
	textAreaWrapper: {
		position: 'relative',
	},
});
